import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import * as env from 'config/environment';
import {
  getSymbolHaitoInfo,
  getNotifications,
  getSymbolYutaiInfo,
} from 'config/api';
import { formatDateForString, formatNumber, formatDate } from 'utils/formatter';
import { useRouting } from 'hooks/useRouting';
import { convertyyyymmToDate } from 'pages/DividendDeposit';
import Loading from 'components/commons/loading/Loading';
import {
  AccountType,
  AccountTypeLabel,
  accountTypeToString,
} from '../utils/accountType';
import { DividendListProps } from '../dividendPrediction/DividendClassification';
import { getSummaryDividendHaito } from '../dividendPrediction/DividendTransition';
import {
  SymbolHaitoInfoProps,
  showNewIcon,
  NotificationsProps,
  HightDividendLink,
  ShareholderBenefitsLink,
} from '../dividendDetailDeposit/DividendDetailInfo';

const DividendDetailInfoInternal = ({
  dividendList,
  symbolHaitoInfo,
  notifications,
  yutaiInfo,
  isTaxDeduction,
  isFetchingSymbolHaitoInfo,
  isFetchingNotifications,
  isFetchingYutaiInfo,
  onCloseRequest,
}: {
  dividendList: DividendListProps;
  symbolHaitoInfo: SymbolHaitoInfoProps;
  notifications: NotificationsProps[] | null;
  yutaiInfo: YutaiInfoProps | null;
  isTaxDeduction: boolean;
  isFetchingSymbolHaitoInfo: boolean;
  isFetchingNotifications: boolean;
  isFetchingYutaiInfo: boolean;
  onCloseRequest: () => void;
}) => {
  const summaryDividend = getSummaryDividendHaito(
    dividendList.items,
    isTaxDeduction
  );

  const summaryQty = dividendList.items
    .map(item => item.qty)
    .reduce((acc, current) => acc + current, 0);

  const isIncludeUnreleasedDividend = dividendList.items.some(
    item =>
      item.unreleasedStatus === 'all' || item.unreleasedStatus === 'not_all'
  );
  const isAllUnreleasedDividend = dividendList.items.every(
    item => item.unreleasedStatus === 'all'
  );
  const unreleasedStatus =
    !isIncludeUnreleasedDividend && !isAllUnreleasedDividend
      ? 'not_exist'
      : isIncludeUnreleasedDividend && !isAllUnreleasedDividend
      ? 'not_all'
      : 'all';

  const routing = useRouting();
  const goDetail = (id: string) => {
    return (): void => {
      routing.pushRouting(
        { path: `/notificationDetail/4/${id}/${dividendList.symbol}/DIVIDEND` },
        true,
        window.scrollY
      );
    };
  };

  const getDate = (): string[] | null => {
    if (
      symbolHaitoInfo === null ||
      symbolHaitoInfo === undefined ||
      symbolHaitoInfo.kessanYm === null
    )
      return null;
    const data = convertyyyymmToDate(symbolHaitoInfo.kessanYm);
    if (data === null) return null;

    const year = formatDate(data, 'yyyy');
    const month = formatDate(data, 'MM');
    return [year, month];
  };
  const date = getDate();

  return (
    <div className="w-full flex flex-col py-2 text-bluegray-700">
      <div className="flex flex-row pb-2 px-2">
        <div
          className="icon-close text-bluegray-400 ml-auto text-3xl cursor-pointer"
          onClick={onCloseRequest}></div>
      </div>
      <div className="flex flex-row items-center px-2">
        <div className="text-xs text-bluegray-500">{dividendList.symbol}</div>
        <div className="flex flex-row">
          {dividendList.items.map((data, i) => {
            return (
              <AccountTypeLabel
                accountType={data.accountType as AccountType}
                className="ml-1"
                key={i}
              />
            );
          })}
        </div>
      </div>
      <div className="mt-4 mx-2 pb-1 text-lg font-bold text-blue-850 border-b border-bluegray-300">
        {dividendList.symbolName}
      </div>
      <div className="flex flex-row mt-4 px-2 text-xs text-bluegray-500">
        <div>
          配当金額
          {unreleasedStatus !== 'not_exist' ? (
            <span className="ml-1">
              (未発表{unreleasedStatus === 'not_all' ? '含む' : ''})
            </span>
          ) : (
            <></>
          )}
        </div>
        <div className="ml-auto">数量</div>
        <div className="ml-2">
          {formatNumber(summaryQty, {})}
          <span className="ml-1">株</span>
        </div>
      </div>
      <div className="mt-2 px-2 text-36">
        {formatNumber(summaryDividend, {})}
        <span className="ml-1 text-2xl">円</span>
      </div>
      <div className="mt-2 px-2">
        {dividendList.items.map((data, i) => {
          return (
            <React.Fragment key={i}>
              <div className="h-14 flex flx-row items-center px-4 py-2 mb-1 bg-neutral-100">
                <div className="text-xs font-bold">
                  {accountTypeToString(data.accountType as AccountType)}
                </div>
                <div
                  className={`flex flex-col ml-auto ${
                    data.unreleasedStatus === 'all' ? 'text-bluegray-400' : ''
                  }`}>
                  <div>
                    {formatNumber(
                      isTaxDeduction ? data.taxExcludedHaito : data.haito,
                      {}
                    )}
                    <span className="ml-1 text-sm">円</span>
                  </div>
                  {data.unreleasedStatus !== 'not_exist' ? (
                    <div className="ml-auto text-1.5xs">
                      (未発表
                      {data.unreleasedStatus === 'not_all' ? '含む' : ''})
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      {symbolHaitoInfo !== null &&
      symbolHaitoInfo !== undefined &&
      yutaiInfo !== null &&
      yutaiInfo !== undefined &&
      !isFetchingSymbolHaitoInfo &&
      !isFetchingYutaiInfo ? (
        <>
          <div
            className="grid items-center mt-2 mx-2 border-t border-b border-gray-210"
            style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="pl-2 py-1.5 text-xs font-bold bg-bluegray-125">
              配当利回り
            </div>
            <div className="ml-auto pr-4 text-sm">
              {symbolHaitoInfo.yield === null
                ? '---'
                : symbolHaitoInfo.yield.toFixed(2)}
              %
            </div>
          </div>
          <div
            className="grid items-center mx-2 border-b border-gray-210"
            style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="pl-2 py-1.5 text-xs text-bluegray-700 font-bold bg-bluegray-125">
              {date === null ? (
                <></>
              ) : (
                <>
                  年間配当({date[0]}年{date[1]}月期)
                </>
              )}
            </div>
            <div className="ml-auto pr-4 text-sm">
              {symbolHaitoInfo.yearlyAmount === null
                ? '---'
                : symbolHaitoInfo.yearlyAmount.toFixed(2)}
              円
            </div>
          </div>
          <div
            className="grid items-center mx-2 border-b border-gray-210"
            style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="pl-2 py-1.5 text-xs font-bold bg-bluegray-125">
              権利確定月
            </div>
            <div className="ml-auto pr-4 text-sm">
              {symbolHaitoInfo.cutOffMonth}
            </div>
          </div>
          <div
            className="grid items-center mx-2 border-b border-gray-210"
            style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="pl-2 py-1.5 text-xs font-bold bg-bluegray-125">
              支払予想月
            </div>
            <div className="ml-auto pr-4 text-sm">
              {symbolHaitoInfo.depositMonth}
            </div>
          </div>
          <div
            className="grid items-center mx-2 border-b border-gray-210"
            style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="pl-2 py-1.5 text-xs font-bold bg-bluegray-125">
              優待利回り
            </div>
            <div className="ml-auto pr-4 text-sm">
              {yutaiInfo.yutaiYield === null
                ? '---'
                : Math.trunc(yutaiInfo.yutaiYield * 100) / 100}
              %
            </div>
          </div>
          <div
            className="grid items-center mx-2 border-b border-gray-210"
            style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="h-full flex items-center bg-bluegray-125">
              <div className="pl-2 py-1.5 text-xs font-bold">優待情報</div>
            </div>
            <div className="ml-auto pl-1 pr-4 text-sm">
              {yutaiInfo.yutaiTitle === null ? '---' : yutaiInfo.yutaiTitle}
            </div>
          </div>
        </>
      ) : (
        <Loading
          loading={true}
          circularLoading={true}
          style={{ paddingTop: '60px', paddingBottom: '60px' }}
        />
      )}

      <div
        className="grid mt-4 mx-2"
        style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div className="mr-1">
          <a
            href={`${env.minkabuStockInfoBaseURL}/${dividendList.symbol}`}
            target="_blank"
            rel="noopener noreferrer">
            <button className="w-full flex justify-center items-center py-2 px-auto font-bold text-blue-700 bg-white border border-blue-700 rounded-lg">
              株価情報
              <div className="icon-external-link ml-1"></div>
            </button>
          </a>
        </div>
        <div className="ml-1">
          <a
            href={`${env.minkabuStockInfoBaseURL}/${dividendList.symbol}/dividend`}
            target="_blank"
            rel="noopener noreferrer">
            <button className="w-full flex justify-center items-center py-2 px-auto font-bold text-blue-700 bg-white border border-blue-700 rounded-lg">
              配当情報
              <div className="icon-external-link ml-1"></div>
            </button>
          </a>
        </div>
      </div>
      <div className="mx-2 mt-4 text-xs text-bluegray-500">
        ※ 配当利回りは前日終値で計算しています。
      </div>

      <div className="mt-6 mx-2 font-bold">
        {dividendList.symbolName}を<br />
        保有する方におすすめ
      </div>
      <HightDividendLink
        {...{
          symbolName: dividendList.symbolName,
          yieldNum:
            symbolHaitoInfo === null || symbolHaitoInfo === undefined
              ? 0
              : symbolHaitoInfo.yield,
          tag: 'prediction',
          className: 'mt-2',
        }}
      />
      <ShareholderBenefitsLink
        {...{
          symbol: dividendList.symbol,
          tag: 'prediction',
          className: 'mt-2 mb-4',
        }}
      />

      <div className="mt-12 px-2 font-bold">配当金関連ニュース</div>
      <div className="mt-4 border-t border-gray-300" />
      {notifications !== null &&
      notifications !== undefined &&
      !isFetchingNotifications ? (
        <>
          {notifications.map((data, i) => {
            const title = data.title.replace(/^【.*\)】/g, '');
            return (
              <div
                className="flex flex-row items-center justify-between py-2 px-2 border-b border-gray-300 cursor-pointer"
                onClick={goDetail(data.id)}
                key={i}>
                <div className="flex flex-col text-sm">
                  <div className="line-clamp-1">{title}</div>
                  <div className="text-gray-500">
                    {showNewIcon(data.insTime)}
                    {formatDateForString(data.insTime, 'M/d HH:mm')}
                  </div>
                </div>
                <div className="icon-chevron-right flex text-2xl text-gray-500 ml-2"></div>
              </div>
            );
          })}
        </>
      ) : (
        <Loading
          loading={true}
          circularLoading={true}
          style={{ paddingTop: '120px', paddingBottom: '120px' }}
        />
      )}
      <div className="mt-4 mx-2">
        <a
          href={`${env.minkabuStockInfoBaseURL}/${dividendList.symbol}/news`}
          target="_blank"
          rel="noopener noreferrer">
          <button className="w-full flex justify-center items-center py-2 px-8 mb-16 font-bold text-blue-700 bg-white border border-blue-700 rounded-lg">
            この銘柄のニュースを見る
            <div className="icon-external-link ml-1"></div>
          </button>
        </a>
      </div>
    </div>
  );
};

const GetSymbolHaitoInfo = ({
  dividendList,
  isTaxDeduction,
  onCloseRequest,
}: {
  dividendList: DividendListProps;
  isTaxDeduction: boolean;
  onCloseRequest: () => void;
}) => {
  const {
    data: symbolHaitoInfo,
    isFetching: isFetchingSymbolHaitoInfo,
  } = useQuery(
    'getSymbolHaitoInfoDividendPredictionAPI',
    () => getSymbolHaitoInfoAPI(dividendList.symbol),
    {
      refetchOnWindowFocus: false,
    }
  );

  const func = 'DIVIDEND';
  const { data: notifications, isFetching: isFetchingNotifications } = useQuery(
    'getNotificationsDividendPredictionAPI',
    () => getNotificationsAPI(dividendList.symbol, func),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: yutaiInfo, isFetching: isFetchingYutaiInfo } = useQuery(
    'getSymbolYutaiInfoDividendPredictionAPI',
    () => getSymbolYutaiInfoAPI(dividendList.symbol),
    {
      refetchOnWindowFocus: false,
    }
  );

  return (
    <DividendDetailInfoInternal
      {...{
        dividendList,
        symbolHaitoInfo,
        notifications,
        yutaiInfo,
        isTaxDeduction,
        isFetchingSymbolHaitoInfo,
        isFetchingNotifications,
        isFetchingYutaiInfo,
        onCloseRequest,
      }}
    />
  );
};

export const DividendDetailInfo = ({
  detailInfoHide,
  hideAnimate,
  dividendData,
  isTaxDeduction,
}: {
  detailInfoHide: () => void;
  hideAnimate: boolean;
  dividendData: DividendListProps;
  isTaxDeduction: boolean;
}): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    if (hideAnimate) setIsOpen(false);
  }, [hideAnimate]);

  const onCloseRequest = (): void => {
    setIsOpen(false);
  };

  const onAnimated = (): void => {
    if (!isOpen) detailInfoHide();
  };

  const animationOption =
    ' animation-200ms animation-ease-in-out animation-fill-forwards';

  const showOrHideAnimation = isOpen
    ? { content: 'animation-appear-from-right', mask: 'animation-fadeIn' }
    : { content: 'animation-disappear-to-right', mask: 'animation-fadeOut' };
  showOrHideAnimation.content += animationOption;
  showOrHideAnimation.mask += animationOption;

  return (
    <div
      className="fixed left-0 ui-h-screen w-screen z-20"
      style={{ top: '0' }}>
      <div className="overflow-y-auto relative h-full w-full">
        <div
          className={`fixed left-0 bg-gray-800 h-full w-full ${showOrHideAnimation.mask}`}
          onClick={onCloseRequest}
        />
        <div
          className={`lg:flex hidden fixed right-0 h-full bg-white overflow-y-auto w-64 ${showOrHideAnimation.content}`}
          style={{
            width: '35%',
            height: '100%',
          }}
          onAnimationEnd={onAnimated}>
          <GetSymbolHaitoInfo
            {...{ dividendList: dividendData, isTaxDeduction, onCloseRequest }}
          />
        </div>
        <div
          className={`flex lg:hidden fixed right-0 h-full bg-white overflow-y-auto w-64 ${showOrHideAnimation.content}`}
          style={{
            width: '90%',
            height: '100%',
          }}
          onAnimationEnd={onAnimated}>
          <GetSymbolHaitoInfo
            {...{ dividendList: dividendData, isTaxDeduction, onCloseRequest }}
          />
        </div>
      </div>
    </div>
  );
};

const getSymbolHaitoInfoAPI = async (symbol: string) => {
  const ret_ = await getSymbolHaitoInfo(symbol);
  const ret = {
    // memo: apiレスポンスを yyyy.mm -> yyyymm に変換する
    kessanYm: ret_.KessanYm !== null ? ret_.KessanYm.replace('.', '') : null,
    yield: ret_.Yield,
    yearlyAmount: ret_.YearlyAmount,
    cutOffMonth: ret_.CutOffMonth,
    depositMonth: ret_.DepositMonth,
  } as SymbolHaitoInfoProps;

  return ret;
};

const getNotificationsAPI = async (
  symbol: string,
  func: string
): Promise<NotificationsProps[] | null> => {
  const ret_ = await getNotifications(symbol, func);
  if (ret_ === null) return null;

  const ret = ret_?.Items.map(item => {
    return {
      id: item.ID,
      type: item.Type,
      title: item.Title,
      content: item.Content,
      readStatus: item.ReadStatus,
      insTime: item.InsTime,
      updTime: item.UpdTime,
    };
  }) as NotificationsProps[];

  return ret;
};

export type YutaiInfoProps = {
  yutaiTitle: string | null;
  yutaiYield: number | null;
};

const getSymbolYutaiInfoAPI = async (
  symbol: string
): Promise<YutaiInfoProps | null> => {
  const ret_ = await getSymbolYutaiInfo(symbol);
  if (ret_ === null) return null;

  const ret = {
    yutaiTitle: ret_.YutaiTitle,
    yutaiYield: ret_.YutaiYield,
  } as YutaiInfoProps;

  return ret;
};
